/**
 * demoFirestoreRouter.ts — Single interception point for all Firestore reads in demo mode.
 *
 * CRITICAL DESIGN: In demo mode, all date-filtered queries are rewritten
 * so that "today" = DEMO_ANCHOR (from demoClock). The demo is a recorded
 * film — no dependency on system clock, timezone, or session state.
 *
 * Place at: src/demo/demoFirestoreRouter.ts
 */

import { demoData } from './demoData';
import { demoPersona } from './demoPersona';
import { demoToday, demoYesterday } from './demoClock';

// ── Types ────────────────────────────────────────────────────────

interface ParsedConstraints {
  dateEquals?: string;
  dateGte?: string;
  dateLte?: string;
  limitCount?: number;
  orderByField?: string;
  orderByDir?: 'asc' | 'desc';
}

// ── Collection Registry ──────────────────────────────────────────

type CollectionFactory = () => Record<string, any>[];

const DEMO_COLLECTIONS: Record<string, CollectionFactory> = {
  nutritionEntries: () => demoData.nutrition(),
  supplementEntries: () => demoData.supplementEntries(),
  hydrationEntries: () => demoData.hydrationEntries(),
  workoutEntries: () => demoData.allWorkouts(),
  bodyStatsEntries: () => demoData.bodyStats(),
  bodyMetrics: () => [{
    id: demoPersona.userId,
    ...demoData.bodyMetrics,
  }],
  userProfiles: () => [{
    id: demoPersona.userId,
    ...demoData.profile,
    userId: demoPersona.userId,
    notifications_enabled: true,
    glp1DoseDay: demoPersona.doseDay,
  }],
  targetRanges: () => [{
    id: demoPersona.userId,
    ...demoData.targets,
  }],
  tdeeConfig: () => [{
    id: demoPersona.userId,
    ...demoData.tdeeConfig,
  }],
  deficitPlan: () => [{
    id: demoPersona.userId,
    ...demoData.deficitPlan,
  }],
  nutritionRecentsFavourites: () => [{
    id: demoPersona.userId,
    ...demoData.nutritionRecentsFavourites,
  }],
  workoutRecentsFavourites: () => [{
    id: demoPersona.userId,
    ...demoData.workoutRecentsFavourites,
  }],
  weeks: () => {
    const history = demoData.functionalAgeHistory();
    return history.map((snap, i) => ({
      id: `demo-fa-${i}`,
      ...snap,
    }));
  },
  weeklySnapshots: () => {
    // Body stats screen tries to read/generate weekly snapshots.
    // Return empty so it doesn't try to write one (hits the demo guard).
    return [];
  },
};

// ── Date Rewriting ───────────────────────────────────────────────
// Services query with getTodayDate() (real system date).
// In demo mode, we rewrite that to the demo anchor date.
// "yesterday" queries get rewritten to anchor - 1 day.
// This makes the demo completely independent of the system clock.

function rewriteDateToAnchor(realDate: string): string {
  const anchor = demoToday();
  const anchorYesterday = demoYesterday();

  // If the date already matches the anchor (e.g. getTodayDate() already returned it),
  // pass through unchanged — no double-shift.
  if (realDate === anchor) return anchor;
  if (realDate === anchorYesterday) return anchorYesterday;

  // Get the real "today" from the system
  const now = new Date();
  const realToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // If the query date is "today" (real), rewrite to anchor
  if (realDate === realToday) return anchor;

  // If the query date is "yesterday" (real), rewrite to anchor - 1
  const realYesterday = new Date(now);
  realYesterday.setDate(realYesterday.getDate() - 1);
  const realYesterdayStr = `${realYesterday.getFullYear()}-${String(realYesterday.getMonth() + 1).padStart(2, '0')}-${String(realYesterday.getDate()).padStart(2, '0')}`;
  if (realDate === realYesterdayStr) return anchorYesterday;

  // For other dates, compute the offset from the anchor and apply
  // (dates coming from getTodayDate()-based offsets are relative to anchor)
  const anchorMs = new Date(anchor + 'T12:00:00').getTime();
  const dateMs = new Date(realDate + 'T12:00:00').getTime();
  const offsetDays = Math.round((dateMs - anchorMs) / 86400000);

  // If offset is within demo data range (-20 to 0), pass through unchanged
  // These are already correct anchor-relative dates from getTodayDate()/getDateDaysAgo()
  if (offsetDays <= 0 && offsetDays >= -20) return realDate;

  // Outside range — compute from real today offset applied to anchor
  const realTodayMs = new Date(realToday + 'T12:00:00').getTime();
  const realOffset = Math.round((dateMs - realTodayMs) / 86400000);
  const anchorDate = new Date(anchor + 'T12:00:00');
  anchorDate.setDate(anchorDate.getDate() + realOffset);
  return `${anchorDate.getFullYear()}-${String(anchorDate.getMonth() + 1).padStart(2, '0')}-${String(anchorDate.getDate()).padStart(2, '0')}`;
}

// ── Constraint Parsing ───────────────────────────────────────────

function parseConstraints(constraints: unknown[]): ParsedConstraints {
  const parsed: ParsedConstraints = {};

  for (const c of constraints) {
    if (!c || typeof c !== 'object') continue;
    const constraint = c as Record<string, unknown>;

    const type = constraint.type as string | undefined;
    const hasOp = constraint._op !== undefined || constraint.op !== undefined;

    // where() constraints
    if (type === 'where' || hasOp) {
      const field = extractField(constraint);
      const op = (constraint._op ?? constraint.op ?? '') as string;
      const value = constraint._value ?? constraint.value;

      if (field === 'date' && typeof value === 'string') {
        // REWRITE: real system date → demo anchor date
        const rewritten = rewriteDateToAnchor(value);
        if (op === '==' || op === 'eq') parsed.dateEquals = rewritten;
        if (op === '>=' || op === 'gte') parsed.dateGte = rewritten;
        if (op === '<=' || op === 'lte') parsed.dateLte = rewritten;
      }
      continue;
    }

    // limit()
    if (type === 'limit' || constraint._limit !== undefined) {
      const lim = constraint._limit ?? constraint.limit;
      if (typeof lim === 'number') parsed.limitCount = lim;
      continue;
    }

    // orderBy() — only if not a where
    if (type === 'orderBy' || (constraint._field !== undefined && !hasOp)) {
      const field = extractField(constraint);
      const dir = (constraint._direction ?? constraint.direction ?? 'asc') as string;
      if (field && !parsed.orderByField) {
        parsed.orderByField = field;
        parsed.orderByDir = dir === 'desc' ? 'desc' : 'asc';
      }
    }
  }

  return parsed;
}

function extractField(constraint: Record<string, unknown>): string | null {
  const field = constraint._field ?? constraint.field;
  if (typeof field === 'string') return field;
  if (field && typeof field === 'object') {
    const fp = field as Record<string, unknown>;
    if (Array.isArray(fp.segments)) return (fp.segments as string[])[0] ?? null;
    if (typeof fp._internalPath === 'string') return fp._internalPath;
  }
  return null;
}

// ── Filtering ────────────────────────────────────────────────────

function applyConstraints(
  entries: Record<string, any>[],
  constraints: ParsedConstraints,
): Record<string, any>[] {
  let result = [...entries];

  if (constraints.dateEquals) {
    result = result.filter((e) => e.date === constraints.dateEquals);
  }
  if (constraints.dateGte) {
    result = result.filter((e) => e.date != null && e.date >= constraints.dateGte!);
  }
  if (constraints.dateLte) {
    result = result.filter((e) => e.date != null && e.date <= constraints.dateLte!);
  }
  if (constraints.orderByField) {
    const field = constraints.orderByField;
    const dir = constraints.orderByDir === 'desc' ? -1 : 1;
    result.sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }
  if (constraints.limitCount && constraints.limitCount > 0) {
    result = result.slice(0, constraints.limitCount);
  }

  return result;
}

// ── Public API ───────────────────────────────────────────────────

function resolveCollectionName(path: string): string {
  const segments = path.split('/');
  return segments[segments.length - 1] ?? path;
}

export function demoGetCollection<T>(
  path: string,
  constraints: unknown[],
): T[] | null {
  const name = resolveCollectionName(path);
  const factory = DEMO_COLLECTIONS[name];
  if (!factory) return null;

  const all = factory();
  const parsed = parseConstraints(constraints);
  const filtered = applyConstraints(all, parsed);

  return filtered.map((entry, i) => ({
    id: entry.id ?? `demo-${name}-${i}`,
    ...entry,
  })) as unknown as T[];
}

export function demoGetDocument<T>(
  path: string,
): T | null {
  const segments = path.split('/');
  if (segments.length < 2) return null;

  const collectionName = segments[segments.length - 2];
  const factory = DEMO_COLLECTIONS[collectionName];
  if (!factory) return null;

  const all = factory();
  return (all.length > 0 ? { id: segments[segments.length - 1], ...all[0] } : null) as T | null;
}

export function demoGetDocumentByUserId<T>(
  collectionPath: string,
): T | null {
  const name = resolveCollectionName(collectionPath);
  const factory = DEMO_COLLECTIONS[name];
  if (!factory) return null;

  const all = factory();
  if (all.length === 0) return null;
  return { id: all[0].id ?? demoPersona.userId, ...all[0] } as unknown as T;
}
